import { EqipReferenceAPI, PracticeContentDataFields } from '@cibo/core'
import { findFipsName } from '@cibo/landmanager'
import { SkeletonTable } from '@cibo/ui'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import Download from '@mui/icons-material/Download'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {
  Alert,
  AlertTitle,
  Grid2 as Grid,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import { useQuery } from '@tanstack/react-query'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

type PracticeDrawerProps = {
  resourceConcernList?: string[]
  isSelected?: boolean
  cardButton?: ReactNode
  displaySelectInteraction: boolean
  onClickSelect: (practiceId?: string) => void
  onCloseDrawer: () => void
  open: boolean
  practice?: PracticeContentDataFields
}

export const PracticeDrawer = ({
  practice,
  isSelected,
  onClickSelect,
  displaySelectInteraction,
  open,
  onCloseDrawer,
}: PracticeDrawerProps) => {
  const theme = useTheme()
  const { t } = useTranslation('@cibo/programs/PracticeDrawer')
  const { palette } = useTheme()

  const { engagementId } = useParams()
  const practiceReference = useQuery({
    queryKey: ['practiceReference', practice?.fields?.practiceId],
    queryFn: () =>
      EqipReferenceAPI.getWorkflowPracticeDetails(engagementId as string, {
        selectedPractices: practice?.fields?.practiceId,
        useIntendedPractices: false,
      }),
    enabled: !!practice?.fields?.practiceId,
  })

  let drawerContent: ReactNode = null

  if (!practice) {
    drawerContent = <Typography variant="h6">{t('noPracticeSelected')}</Typography>
  } else if (practiceReference.isLoading || !practiceReference.data) {
    drawerContent = (
      <Grid container spacing={1.5} columns={{ xs: 4, md: 8, lg: 12 }}>
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center">
              <IconButton disabled>
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Typography variant="h6" component="h2" sx={{ width: 300 }}>
                <Skeleton width={200} height={24} />
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              disabled
              startIcon={<AddCircleOutlineIcon />}
              sx={{
                color: palette.text.secondary,
                borderColor: palette.text.primary,
              }}
            >
              {t('addPractice')}
            </Button>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Stack spacing={1.5} paddingBlock={2}>
            <Skeleton width="100%" height={20} />
            <Skeleton width={86} height={20} />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" paddingBottom={2}>
            {t('estimatedPaymentRates')}
          </Typography>
          <SkeletonTable />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Stack spacing={1.5} paddingBlock={2}>
            <Button startIcon={<Download />} disabled style={{ alignSelf: 'flex-start' }}>
              {t('downloadNRCSRequirements')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    )
  } else {
    const isEligible = !practice.tags?.includes('Ineligible')

    drawerContent = (
      <Grid container spacing={1.5} columns={{ xs: 4, md: 8, lg: 12 }}>
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center">
              <IconButton
                onClick={onCloseDrawer}
                aria-label={
                  t('closeDrawer', {
                    practiceName: practice.fields.name,
                  }) as string
                }
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Typography component="h2">
                <Stack direction="column">
                  <Typography variant="h6" component="span">
                    {practiceReference.data.practices[0].practiceName}
                  </Typography>
                  <Typography component="span">
                    {practiceReference.data.fipsCode &&
                      findFipsName(practiceReference.data.fipsCode, t)}
                  </Typography>
                </Stack>
              </Typography>
            </Stack>
            {displaySelectInteraction &&
              (isSelected || !isEligible ? (
                <Button
                  size="small"
                  variant="outlined"
                  data-testid="addRemovePracticeBtn"
                  sx={{
                    color: palette.text.secondary,
                    borderColor: palette.text.primary,
                    minWidth: '150px',
                  }}
                  startIcon={<RemoveCircleOutlineIcon />}
                  onClick={() => {
                    onClickSelect(practice.fields?.practiceId)
                  }}
                  disabled={!isEligible}
                >
                  {isSelected ? t('removePractice') : t('addPractice')}
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => {
                    onClickSelect(practice.fields?.practiceId)
                  }}
                >
                  {t('addPractice')}
                </Button>
              ))}
          </Stack>
        </Grid>
        {!isEligible && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="error">
              <AlertTitle>{t('alertTitle')}</AlertTitle>
              {t('alertContent')}
            </Alert>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Stack spacing={1.5} paddingBlock={2}>
            <Typography variant="body1">{practice.fields?.definition}</Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" paddingBottom={2}>
            {t('estimatedPaymentRates')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('practice')}</TableCell>
                  <TableCell>{t('rate')}</TableCell>
                  <TableCell>{t('rateHU')}</TableCell>
                </TableRow>
              </TableHead>
              {practiceReference.data?.practices[0].payments.map(practice => {
                if (practice.scenario !== 'Disaster Payment Adjustment')
                  return (
                    <TableRow key={practice.scenario}>
                      <TableCell>{practice.scenario}</TableCell>
                      <TableCell>{practice.rateAndUnit}</TableCell>
                      <TableCell>{practice.huRateAndUnit}</TableCell>
                    </TableRow>
                  )
              })}
            </Table>
          </TableContainer>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Stack spacing={1.5} paddingBlock={2}>
            <Button
              component="a"
              target="_blank"
              startIcon={<Download />}
              download
              style={{ alignSelf: 'flex-start' }}
              href={practiceReference.data.practices[0].pdfUrl}
            >
              {t('downloadNRCSRequirements')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onCloseDrawer}
      style={{ zIndex: theme.zIndex.modal + 1 }}
      slotProps={{
        paper: {
          style: {
            width: useMediaQuery(theme.breakpoints.up('lg')) ? '50%' : '100%',
          },
        },
      }}
    >
      <Stack
        role="presentation"
        onClick={onCloseDrawer}
        onKeyDown={onCloseDrawer}
        padding={3}
        direction="column"
      >
        {drawerContent}
      </Stack>
    </Drawer>
  )
}
